"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getInfo = exports.removeDropzone = exports.createDropzone = exports.setBranding = exports.modifyTransfer = exports.showDirectory = exports.getTransfer = exports.getAllTransfers = exports.showPreferences = exports.showSelectFolderDialog = exports.showSelectFileDialog = exports.resumeTransfer = exports.stopTransfer = exports.removeTransfer = exports.deregisterStatusCallback = exports.registerStatusCallback = exports.deregisterActivityCallback = exports.registerActivityCallback = exports.startTransfer = exports.init = exports.initDragDrop = exports.testConnection = void 0;
var messages_1 = require("../constants/messages");
var client_1 = require("../helpers/client/client");
var helpers_1 = require("../helpers/helpers");
var http_gateway_1 = require("../http-gateway");
var core_1 = require("../http-gateway/core");
var index_1 = require("../index");
var http_gateway_sdk_js_1 = require("@ibm-aspera/http-gateway-sdk-js");
var connect_sdk_js_1 = require("@ibm-aspera/connect-sdk-js");
var core_2 = require("../connect/core");
/**
 * Check if IBM Aspera for Desktop connection works. This function is called by init
 * when initializing the SDK. This function can be used at any point for checking.
 *
 * @returns a promise that resolves if server can connect or rejects if not
 */
var testConnection = function () {
    if (index_1.asperaSdk.useHttpGateway || index_1.asperaSdk.useConnect) {
        return Promise.resolve(index_1.asperaSdk.globals.sdkResponseData);
    }
    return client_1.client.request('get_info')
        .then(function (data) {
        index_1.asperaSdk.globals.asperaSdkInfo = data;
        index_1.asperaSdk.globals.asperaAppVerified = true;
        return index_1.asperaSdk.globals.sdkResponseData;
    });
};
exports.testConnection = testConnection;
/**
 * Initialize drag and drop. HTTP Gateway and Connect does not need to init.
 * Ignore if only HTTP Gateway
 * @param initCall - Indicate if called via init flow and should not reject
 *
 * @returns a promise that resolves if the initialization was successful or not
 */
var initDragDrop = function (initCall) {
    if (index_1.asperaSdk.useHttpGateway || index_1.asperaSdk.useConnect) {
        return Promise.resolve(true);
    }
    else if (!index_1.asperaSdk.isReady) {
        return (0, helpers_1.throwError)(messages_1.messages.serverNotVerified);
    }
    var promiseInfo = (0, helpers_1.generatePromiseObjects)();
    client_1.client.request('init_drag_drop')
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        (0, helpers_1.errorLog)(messages_1.messages.dragDropInitFailed, error);
        if (initCall) {
            promiseInfo.resolver(false);
            (0, helpers_1.errorLog)(messages_1.messages.dragDropInitFailedInit, error);
        }
        else {
            promiseInfo.rejecter((0, helpers_1.generateErrorBody)(messages_1.messages.dragDropInitFailed, error));
        }
    });
    return promiseInfo.promise;
};
exports.initDragDrop = initDragDrop;
/**
 * Initialize IBM Aspera client. If client cannot (reject/catch), then
 * client should attempt fixing server URL or trying again. If still fails disable UI elements.
 *
 * @param options initialization options:
 *
 * - `appId` the unique ID for the website. Transfers initiated during this session
 * will be associated with this ID. It is recommended to use a unique ID to keep transfer
 * information private from other websites.
 *
 * - `supportMultipleUsers` when enabled (defaults to false), the SDK will iterate over a port
 * range and generate a session id to determine the running instance of the desktop app for the
 * current user. This is needed when multiple users may be logged into the same machine
 * simultaneously, for example on a Windows Server.
 *
 * @returns a promise that resolves if IBM Aspera Desktop is running properly or
 * rejects if unable to connect
 */
var init = function (options) {
    var _a, _b, _c;
    var appId = (_a = options === null || options === void 0 ? void 0 : options.appId) !== null && _a !== void 0 ? _a : (0, helpers_1.randomUUID)();
    index_1.asperaSdk.globals.appId = appId;
    // Watch for old HTTP Gateway transfers in case used.
    (0, http_gateway_sdk_js_1.registerActivityCallback)(function (oldHttpTransfers) {
        oldHttpTransfers.transfers.forEach(function (oldHttpTransfer) {
            (0, core_1.sendTransferUpdate)(oldHttpTransfer);
        });
    });
    // For now ignore multi user support in Safari
    if ((options === null || options === void 0 ? void 0 : options.supportMultipleUsers) && !(0, helpers_1.isSafari)()) {
        index_1.asperaSdk.globals.supportMultipleUsers = true;
        index_1.asperaSdk.globals.sessionId = (0, helpers_1.randomUUID)();
    }
    var handleErrors = function (error) {
        (0, helpers_1.errorLog)(messages_1.messages.serverError, error);
        index_1.asperaSdk.globals.asperaAppVerified = false;
        throw (0, helpers_1.generateErrorBody)(messages_1.messages.serverError, error);
    };
    var getConnectStartCalls = function () {
        index_1.asperaSdk.globals.connect = new connect_sdk_js_1.Connect({
            minVersion: options.connectSettings.minVersion || '3.10.1',
            dragDropEnabled: options.connectSettings.dragDropEnabled,
            connectMethod: options.connectSettings.method,
        });
        index_1.asperaSdk.globals.connectInstaller = new connect_sdk_js_1.ConnectInstaller({
            sdkLocation: options.connectSettings.sdkLocation,
            correlationId: options.connectSettings.correlationId,
            style: 'carbon',
        });
        index_1.asperaSdk.globals.connectAW4 = {
            Connect: connect_sdk_js_1.Connect,
            ConnectInstaller: connect_sdk_js_1.ConnectInstaller,
        };
        return (0, core_2.initConnect)(!options.connectSettings.hideIncludedInstaller);
    };
    var getDesktopStartCalls = function () {
        return index_1.asperaSdk.activityTracking.setup()
            .then(function () { return (0, exports.testConnection)(); })
            .then(function () { return (0, exports.initDragDrop)(true); })
            .then(function () { return index_1.asperaSdk.globals.sdkResponseData; })
            .catch(handleErrors);
    };
    if (((_b = options === null || options === void 0 ? void 0 : options.httpGatewaySettings) === null || _b === void 0 ? void 0 : _b.url) && !index_1.asperaSdk.globals.httpGatewayVerified) {
        var finalHttpGatewayUrl = options.httpGatewaySettings.url.trim();
        if (finalHttpGatewayUrl.indexOf('http') !== 0) {
            finalHttpGatewayUrl = "https://".concat(finalHttpGatewayUrl);
        }
        if (finalHttpGatewayUrl.endsWith('/')) {
            finalHttpGatewayUrl = finalHttpGatewayUrl.slice(0, -1);
        }
        index_1.asperaSdk.globals.httpGatewayUrl = finalHttpGatewayUrl;
        return fetch("".concat(index_1.asperaSdk.globals.httpGatewayUrl, "/info"), { method: 'GET' }).then(function (response) {
            return response.json().then(function (responseData) {
                if (response.status >= 400) {
                    throw Error(responseData);
                }
                return responseData;
            });
        }).then(function (response) {
            var _a, _b;
            index_1.asperaSdk.globals.httpGatewayInfo = response;
            index_1.asperaSdk.globals.httpGatewayVerified = true;
            var iframeContainer = document.createElement('div');
            iframeContainer.id = 'aspera-http-gateway-iframes';
            iframeContainer.style = 'display: none;';
            document.body.appendChild(iframeContainer);
            index_1.asperaSdk.globals.httpGatewayIframeContainer = iframeContainer;
            if ((_a = options === null || options === void 0 ? void 0 : options.httpGatewaySettings) === null || _a === void 0 ? void 0 : _a.forceGateway) {
                return Promise.resolve(index_1.asperaSdk.globals.sdkResponseData);
            }
            else {
                return ((_b = options === null || options === void 0 ? void 0 : options.connectSettings) === null || _b === void 0 ? void 0 : _b.useConnect) ? getConnectStartCalls() : getDesktopStartCalls();
            }
        }).catch(function (error) {
            var _a;
            // If HTTP Gateway fails log and move on to desktop
            (0, helpers_1.errorLog)(messages_1.messages.httpInitFail, error);
            return ((_a = options === null || options === void 0 ? void 0 : options.connectSettings) === null || _a === void 0 ? void 0 : _a.useConnect) ? getConnectStartCalls() : getDesktopStartCalls();
        });
    }
    return ((_c = options === null || options === void 0 ? void 0 : options.connectSettings) === null || _c === void 0 ? void 0 : _c.useConnect) ? getConnectStartCalls() : getDesktopStartCalls();
};
exports.init = init;
/**
 * Start a transfer
 *
 * @param transferSpec standard transferSpec for transfer
 * @param asperaSdkSpec IBM Aspera settings when starting a transfer.
 *
 * @returns a promise that resolves if transfer initiation is successful and rejects if transfer cannot be started
 */
var startTransfer = function (transferSpec, asperaSdkSpec) {
    if (!(0, helpers_1.isValidTransferSpec)(transferSpec)) {
        return (0, helpers_1.throwError)(messages_1.messages.notValidTransferSpec, { transferSpec: transferSpec });
    }
    if (index_1.asperaSdk.useHttpGateway) {
        return transferSpec.direction === 'receive' ? (0, http_gateway_1.httpDownload)(transferSpec, asperaSdkSpec) : (0, http_gateway_1.httpUpload)(transferSpec, asperaSdkSpec);
    }
    else if (index_1.asperaSdk.useConnect) {
        return index_1.asperaSdk.globals.connect.startTransferPromise(transferSpec, asperaSdkSpec).then(function (response) {
            return response.transfer_specs[0];
        });
    }
    else if (!index_1.asperaSdk.isReady) {
        return (0, helpers_1.throwError)(messages_1.messages.serverNotVerified);
    }
    var promiseInfo = (0, helpers_1.generatePromiseObjects)();
    var payload = {
        transfer_spec: transferSpec,
        desktop_spec: asperaSdkSpec,
        app_id: index_1.asperaSdk.globals.appId,
    };
    client_1.client.request('start_transfer', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        (0, helpers_1.errorLog)(messages_1.messages.transferFailed, error);
        promiseInfo.rejecter((0, helpers_1.generateErrorBody)(messages_1.messages.transferFailed, error));
    });
    return promiseInfo.promise;
};
exports.startTransfer = startTransfer;
/**
 * Register a callback event for getting transfer updates
 *
 * @param callback callback function to receive transfers
 *
 * @returns ID representing the callback for deregistration purposes
 */
var registerActivityCallback = function (callback) {
    return index_1.asperaSdk.activityTracking.setCallback(callback);
};
exports.registerActivityCallback = registerActivityCallback;
/**
 * Remove a callback from the transfer callback
 *
 * @param id the ID returned by `registerActivityCallback`
 */
var deregisterActivityCallback = function (id) {
    index_1.asperaSdk.activityTracking.removeCallback(id);
};
exports.deregisterActivityCallback = deregisterActivityCallback;
/**
 * Register a callback for getting updates about the connection status of IBM Aspera SDK.
 *
 * For example, to be notified of when the SDK loses connection with the application or connection
 * is re-established. This can be useful if you want to handle the case where the user quits IBM Aspera
 * after `init` has already been called, and want to prompt the user to relaunch the application.
 *
 * @param callback callback function to receive events
 *
 * @returns ID representing the callback for deregistration purposes
 */
var registerStatusCallback = function (callback) {
    return index_1.asperaSdk.activityTracking.setWebSocketEventCallback(callback);
};
exports.registerStatusCallback = registerStatusCallback;
/**
 * Remove a callback from getting connection status events.
 *
 * @param id the ID returned by `registerStatusCallback`
 */
var deregisterStatusCallback = function (id) {
    index_1.asperaSdk.activityTracking.removeWebSocketEventCallback(id);
};
exports.deregisterStatusCallback = deregisterStatusCallback;
/**
 * Remove a transfer. This will stop the transfer if it is in progress.
 *
 * @param id transfer uuid
 *
 * @returns a promise that resolves if transfer is removed and rejects if transfer cannot be removed
 */
var removeTransfer = function (id) {
    if (index_1.asperaSdk.useHttpGateway) {
        return (0, core_1.httpRemoveTransfer)(id);
    }
    else if (index_1.asperaSdk.useConnect) {
        return index_1.asperaSdk.globals.connect.removeTransfer(id);
    }
    if (!index_1.asperaSdk.isReady) {
        return (0, helpers_1.throwError)(messages_1.messages.serverNotVerified);
    }
    var promiseInfo = (0, helpers_1.generatePromiseObjects)();
    var payload = {
        transfer_id: id,
    };
    client_1.client.request('remove_transfer', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        (0, helpers_1.errorLog)(messages_1.messages.removeTransferFailed, error);
        promiseInfo.rejecter((0, helpers_1.generateErrorBody)(messages_1.messages.removeTransferFailed, error));
    });
    return promiseInfo.promise;
};
exports.removeTransfer = removeTransfer;
/**
 * Stop a transfer.
 *
 * @param id transfer uuid
 *
 * @returns a promise that resolves if transfer is stopped and rejects if transfer cannot be stopped
 */
var stopTransfer = function (id) {
    if (index_1.asperaSdk.useConnect) {
        return index_1.asperaSdk.globals.connect.stopTransfer(id);
    }
    if (!index_1.asperaSdk.isReady) {
        return (0, helpers_1.throwError)(messages_1.messages.serverNotVerified);
    }
    var promiseInfo = (0, helpers_1.generatePromiseObjects)();
    var payload = {
        transfer_id: id,
    };
    client_1.client.request('stop_transfer', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        (0, helpers_1.errorLog)(messages_1.messages.stopTransferFailed, error);
        promiseInfo.rejecter((0, helpers_1.generateErrorBody)(messages_1.messages.stopTransferFailed, error));
    });
    return promiseInfo.promise;
};
exports.stopTransfer = stopTransfer;
/**
 * Resume a paused or failed transfer.
 *
 * @param id transfer uuid
 * @param options resume transfer options
 *
 * @returns a promise that resolves with the new transfer object if transfer is resumed
 */
var resumeTransfer = function (id, options) {
    if (index_1.asperaSdk.useConnect) {
        return index_1.asperaSdk.globals.connect.resumeTransfer(id, options).then(function (response) {
            return response.transfer_spec;
        });
    }
    if (!index_1.asperaSdk.isReady) {
        return (0, helpers_1.throwError)(messages_1.messages.serverNotVerified);
    }
    var promiseInfo = (0, helpers_1.generatePromiseObjects)();
    var payload = {
        transfer_id: id,
        transfer_spec: options,
    };
    client_1.client.request('resume_transfer', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        (0, helpers_1.errorLog)(messages_1.messages.resumeTransferFailed, error);
        promiseInfo.rejecter((0, helpers_1.generateErrorBody)(messages_1.messages.resumeTransferFailed, error));
    });
    return promiseInfo.promise;
};
exports.resumeTransfer = resumeTransfer;
/**
 * Displays a file browser dialog for the user to select files.
 *
 * @param options file dialog options
 *
 * @returns a promise that resolves with the selected file(s) and rejects if user cancels dialog
 */
var showSelectFileDialog = function (options) {
    if (index_1.asperaSdk.useHttpGateway) {
        return (0, core_1.httpGatewaySelectFileFolderDialog)(options, false);
    }
    else if (index_1.asperaSdk.useConnect) {
        return index_1.asperaSdk.globals.connect.showSelectFileDialogPromise(options).then(function (response) {
            return response;
        });
    }
    else if (!index_1.asperaSdk.isReady) {
        return (0, helpers_1.throwError)(messages_1.messages.serverNotVerified);
    }
    var promiseInfo = (0, helpers_1.generatePromiseObjects)();
    var payload = {
        options: options || {},
        app_id: index_1.asperaSdk.globals.appId,
    };
    client_1.client.request('show_file_dialog', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        (0, helpers_1.errorLog)(messages_1.messages.showSelectFileDialogFailed, error);
        promiseInfo.rejecter((0, helpers_1.generateErrorBody)(messages_1.messages.showSelectFileDialogFailed, error));
    });
    return promiseInfo.promise;
};
exports.showSelectFileDialog = showSelectFileDialog;
/**
 * Displays a folder browser dialog for the user to select folders.
 *
 * @param options folder dialog options
 *
 * @returns a promise that resolves with the selected folder(s) and rejects if user cancels dialog
 */
var showSelectFolderDialog = function (options) {
    if (index_1.asperaSdk.useHttpGateway) {
        return (0, core_1.httpGatewaySelectFileFolderDialog)(options, true);
    }
    else if (index_1.asperaSdk.useConnect) {
        return index_1.asperaSdk.globals.connect.showSelectFolderDialogPromise(options).then(function (response) {
            return response;
        });
    }
    else if (!index_1.asperaSdk.isReady) {
        return (0, helpers_1.throwError)(messages_1.messages.serverNotVerified);
    }
    var promiseInfo = (0, helpers_1.generatePromiseObjects)();
    var payload = {
        options: options || {},
        app_id: index_1.asperaSdk.globals.appId,
    };
    client_1.client.request('show_folder_dialog', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        (0, helpers_1.errorLog)(messages_1.messages.showSelectFolderDialogFailed, error);
        promiseInfo.rejecter((0, helpers_1.generateErrorBody)(messages_1.messages.showSelectFolderDialogFailed, error));
    });
    return promiseInfo.promise;
};
exports.showSelectFolderDialog = showSelectFolderDialog;
/**
 * Opens the IBM Aspera preferences page.
 *
 * @returns a promise that resolves when the preferences page is opened.
 */
var showPreferences = function () {
    if (index_1.asperaSdk.useConnect) {
        return index_1.asperaSdk.globals.connect.showPreferences();
    }
    if (!index_1.asperaSdk.isReady) {
        return (0, helpers_1.throwError)(messages_1.messages.serverNotVerified);
    }
    var promiseInfo = (0, helpers_1.generatePromiseObjects)();
    client_1.client.request('open_preferences')
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        (0, helpers_1.errorLog)(messages_1.messages.showPreferencesFailed, error);
        promiseInfo.rejecter((0, helpers_1.generateErrorBody)(messages_1.messages.showPreferencesFailed, error));
    });
    return promiseInfo.promise;
};
exports.showPreferences = showPreferences;
/**
 * Get all transfers associated with the current application.
 *
 * @returns a promise that resolves with an array of transfers.
 */
var getAllTransfers = function () {
    var promiseInfo = (0, helpers_1.generatePromiseObjects)();
    if (index_1.asperaSdk.useHttpGateway) {
        return Promise.resolve((0, core_1.httpGetAllTransfers)());
    }
    else if (index_1.asperaSdk.useConnect) {
        index_1.asperaSdk.globals.connect.getAllTransfers({
            success: function (data) {
                promiseInfo.resolver(data.transfers);
            }, error: function (error) {
                promiseInfo.rejecter(error);
            },
        });
        return promiseInfo.promise;
    }
    if (!index_1.asperaSdk.isReady) {
        return (0, helpers_1.throwError)(messages_1.messages.serverNotVerified);
    }
    var payload = {
        app_id: index_1.asperaSdk.globals.appId,
    };
    client_1.client.request('get_all_transfers', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        (0, helpers_1.errorLog)(messages_1.messages.getAllTransfersFailed, error);
        promiseInfo.rejecter((0, helpers_1.generateErrorBody)(messages_1.messages.getAllTransfersFailed, error));
    });
    return promiseInfo.promise;
};
exports.getAllTransfers = getAllTransfers;
/**
 * Get a specific transfer by ID.
 *
 * @param id transfer uuid
 *
 * @returns a promise that resolves with the transfer.
 */
var getTransfer = function (id) {
    if (index_1.asperaSdk.useHttpGateway) {
        var transfer = (0, core_1.httpGetTransfer)(id);
        if (transfer) {
            return Promise.resolve(transfer);
        }
        else {
            return Promise.reject((0, helpers_1.generateErrorBody)(messages_1.messages.getTransferFailed, { reason: 'Not found' }));
        }
    }
    else if (index_1.asperaSdk.useConnect) {
        return index_1.asperaSdk.globals.connect.getTransfer(id).then(function (response) {
            return response.transfer_info;
        });
    }
    if (!index_1.asperaSdk.isReady) {
        return (0, helpers_1.throwError)(messages_1.messages.serverNotVerified);
    }
    var promiseInfo = (0, helpers_1.generatePromiseObjects)();
    var payload = {
        transfer_id: id,
    };
    client_1.client.request('get_transfer', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        (0, helpers_1.errorLog)(messages_1.messages.getTransferFailed, error);
        promiseInfo.rejecter((0, helpers_1.generateErrorBody)(messages_1.messages.getTransferFailed, error));
    });
    return promiseInfo.promise;
};
exports.getTransfer = getTransfer;
/**
 * Opens and highlights the downloaded file in Finder or Windows Explorer. If multiple files,
 * then only the first file will be selected.
 *
 * @param id transfer uuid
 *
 * @returns a promise that resolves if the file can be shown and rejects if not
 */
var showDirectory = function (id) {
    if (index_1.asperaSdk.useConnect) {
        return index_1.asperaSdk.globals.connect.showDirectory(id);
    }
    if (!index_1.asperaSdk.isReady) {
        return (0, helpers_1.throwError)(messages_1.messages.serverNotVerified);
    }
    var promiseInfo = (0, helpers_1.generatePromiseObjects)();
    var payload = {
        transfer_id: id,
    };
    client_1.client.request('show_directory', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        (0, helpers_1.errorLog)(messages_1.messages.showDirectoryFailed, error);
        promiseInfo.rejecter((0, helpers_1.generateErrorBody)(messages_1.messages.showDirectoryFailed, error));
    });
    return promiseInfo.promise;
};
exports.showDirectory = showDirectory;
/**
 * Modify the speed of a running transfer.
 *
 * @param id transfer uuid
 * @param options transfer rate options
 *
 * @returns a promise that resolves if the transfer rate can be modified and rejects if not
 */
var modifyTransfer = function (id, options) {
    if (index_1.asperaSdk.useConnect) {
        return index_1.asperaSdk.globals.connect.modifyTransfer(id, options).then(function (response) {
            return response;
        });
    }
    if (!index_1.asperaSdk.isReady) {
        return (0, helpers_1.throwError)(messages_1.messages.serverNotVerified);
    }
    var promiseInfo = (0, helpers_1.generatePromiseObjects)();
    var payload = {
        transfer_id: id,
        transfer_spec: options,
    };
    client_1.client.request('modify_transfer', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        (0, helpers_1.errorLog)(messages_1.messages.modifyTransferFailed, error);
        promiseInfo.rejecter((0, helpers_1.generateErrorBody)(messages_1.messages.modifyTransferFailed, error));
    });
    return promiseInfo.promise;
};
exports.modifyTransfer = modifyTransfer;
/**
 * Set the custom branding template to be used by IBM Aspera. If the app is already
 * configured to use a different branding, then the branding template you specify will be
 * stored by the app, allowing the end user to switch at any point.
 *
 * @param id custom branding template id. This should be consistent across page loads.
 * @param options custom branding options
 *
 * @returns a promise that resolves if the branding was properly set.
 */
var setBranding = function (id, options) {
    if (!index_1.asperaSdk.isReady) {
        return (0, helpers_1.throwError)(messages_1.messages.serverNotVerified);
    }
    var promiseInfo = (0, helpers_1.generatePromiseObjects)();
    var branding = {
        id: id,
        name: options.name,
        theme: options.theme,
    };
    var payload = {
        branding: branding,
    };
    client_1.client.request('update_branding', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        (0, helpers_1.errorLog)(messages_1.messages.setBrandingFailed, error);
        promiseInfo.rejecter((0, helpers_1.generateErrorBody)(messages_1.messages.setBrandingFailed, error));
    });
    return promiseInfo.promise;
};
exports.setBranding = setBranding;
/**
 * Create a dropzone for the given element selector.
 *
 * @param callback the function to call once the files are dropped
 * @param elementSelector the selector of the element on the page that should watch for drop events
 * @param connectOptions options for connect
 */
var createDropzone = function (callback, elementSelector, connectOptions) {
    if (index_1.asperaSdk.useConnect) {
        index_1.asperaSdk.globals.connect.setDragDropTargets(elementSelector, connectOptions, function (result) {
            callback({
                event: result.event,
                files: result.files,
            });
        });
        return;
    }
    var elements = document.querySelectorAll(elementSelector);
    if (!elements || !elements.length) {
        (0, helpers_1.errorLog)(messages_1.messages.unableToFindElementOnPage);
        return;
    }
    var dragEvent = function (event) {
        event.preventDefault();
    };
    var dropEvent = function (event) {
        event.preventDefault();
        if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length && event.dataTransfer.files[0]) {
            var files = [];
            for (var i = 0; i < event.dataTransfer.files.length; i++) {
                var file = event.dataTransfer.files[i];
                files.push({
                    lastModified: file.lastModified,
                    name: file.name,
                    size: file.size,
                    type: file.type
                });
            }
            var payload = {
                files: files,
                app_id: index_1.asperaSdk.globals.appId,
            };
            if (index_1.asperaSdk.isReady) {
                client_1.client.request('dropped_files', payload)
                    .then(function (data) { return callback({ event: event, files: data }); })
                    .catch(function (error) {
                    (0, helpers_1.errorLog)(messages_1.messages.unableToReadDropped, error);
                });
            }
            else if (index_1.asperaSdk.httpGatewayIsReady) {
                (0, core_1.handleHttpGatewayDrop)(event.dataTransfer.items, callback, event);
            }
        }
    };
    elements.forEach(function (element) {
        element.addEventListener('dragover', dragEvent);
        element.addEventListener('drop', dropEvent);
        index_1.asperaSdk.globals.dropZonesCreated.set(elementSelector, [{ event: 'dragover', callback: dragEvent }, { event: 'drop', callback: dropEvent }]);
    });
};
exports.createDropzone = createDropzone;
/**
 * Remove dropzone.
 *
 * @param elementSelector the selector of the element on the page that should remove
 */
var removeDropzone = function (elementSelector) {
    var foundDropzone = index_1.asperaSdk.globals.dropZonesCreated.get(elementSelector);
    if (foundDropzone) {
        foundDropzone.forEach(function (data) {
            var elements = document.querySelectorAll(elementSelector);
            if (elements && elements.length) {
                elements.forEach(function (element) {
                    element.removeEventListener(data.event, data.callback);
                });
            }
        });
    }
};
exports.removeDropzone = removeDropzone;
/**
 * Get metadata about the IBM Aspera installation.
 *
 * @returns a promise that returns information about the user's IBM Aspera installation.
 */
var getInfo = function () {
    if (index_1.asperaSdk.useHttpGateway || index_1.asperaSdk.useConnect) {
        return Promise.resolve(index_1.asperaSdk.globals.sdkResponseData);
    }
    if (!index_1.asperaSdk.isReady) {
        return (0, helpers_1.throwError)(messages_1.messages.serverNotVerified);
    }
    return new Promise(function (resolve, _) {
        resolve(index_1.asperaSdk.globals.sdkResponseData);
    });
};
exports.getInfo = getInfo;
